import React, { useState } from 'react';
import { X, Upload, Loader2 } from 'lucide-react';
import { stylistAPI } from '../../../../services/api';

const CreateRequestModal = ({ clientId, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    request_type: 'look',
    description: '',
    budget: '',
    deadline: ''
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    try {
      const uploadPromises = files.map(file => stylistAPI.uploadImage(file));
      const results = await Promise.all(uploadPromises);
      const newUrls = results.map(r => r.url);
      setPhotos([...photos, ...newUrls]);
    } catch (err) {
      setError('Ошибка загрузки фото');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      setError('Введите описание запроса');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const requestData = {
        request_type: formData.request_type,
        description: formData.description.trim(),
        photos: photos,
        budget: formData.budget.trim() || null,
        deadline: formData.deadline || null
      };

      const result = await stylistAPI.createClientRequest(clientId, requestData);
      onCreated(result);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка создания запроса');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-l)',
        zIndex: 1000
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--white)',
          padding: 'var(--space-xxl)',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-xl)'
        }}>
          <h3 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem',
            fontWeight: 400
          }}>
            Создать запрос
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={24} />
          </button>
        </div>

        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.875rem',
          color: 'var(--mid-gray)',
          marginBottom: 'var(--space-l)'
        }}>
          Создайте запрос от имени клиента (например, из мессенджера)
        </p>

        <form onSubmit={handleSubmit}>
          {/* Request Type */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{
              display: 'block',
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 'var(--space-s)',
              color: 'var(--mid-gray)'
            }}>
              Тип запроса *
            </label>
            <div style={{ display: 'flex', gap: 'var(--space-m)' }}>
              <label style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'var(--space-m)',
                border: formData.request_type === 'look' 
                  ? '2px solid var(--black)' 
                  : '1px solid var(--soft-gray)',
                backgroundColor: formData.request_type === 'look' 
                  ? 'var(--cream)' 
                  : 'var(--white)',
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem'
              }}>
                <input
                  type="radio"
                  name="request_type"
                  value="look"
                  checked={formData.request_type === 'look'}
                  onChange={(e) => setFormData({ ...formData, request_type: e.target.value })}
                  style={{ display: 'none' }}
                />
                Подбор образа
              </label>
              <label style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'var(--space-m)',
                border: formData.request_type === 'shopping' 
                  ? '2px solid var(--black)' 
                  : '1px solid var(--soft-gray)',
                backgroundColor: formData.request_type === 'shopping' 
                  ? 'var(--cream)' 
                  : 'var(--white)',
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem'
              }}>
                <input
                  type="radio"
                  name="request_type"
                  value="shopping"
                  checked={formData.request_type === 'shopping'}
                  onChange={(e) => setFormData({ ...formData, request_type: e.target.value })}
                  style={{ display: 'none' }}
                />
                Рекомендация к покупке
              </label>
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{
              display: 'block',
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 'var(--space-s)',
              color: 'var(--mid-gray)'
            }}>
              Описание запроса *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Опишите, что нужно клиенту..."
              rows={4}
              style={{
                width: '100%',
                padding: 'var(--space-m)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                border: '1px solid var(--soft-gray)',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Photos */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{
              display: 'block',
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 'var(--space-s)',
              color: 'var(--mid-gray)'
            }}>
              Фото (необязательно)
            </label>
            
            {photos.length > 0 && (
              <div style={{
                display: 'flex',
                gap: 'var(--space-s)',
                flexWrap: 'wrap',
                marginBottom: 'var(--space-m)'
              }}>
                {photos.map((photo, idx) => (
                  <div key={idx} style={{ position: 'relative' }}>
                    <img
                      src={photo}
                      alt=""
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'cover'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(idx)}
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--black)',
                        color: 'var(--white)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-s)',
              padding: '10px 16px',
              backgroundColor: 'var(--cream)',
              border: 'none',
              fontFamily: 'var(--font-body)',
              fontSize: '0.8125rem',
              cursor: uploading ? 'wait' : 'pointer'
            }}>
              {uploading ? (
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Upload size={16} />
              )}
              {uploading ? 'Загрузка...' : 'Загрузить фото'}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                disabled={uploading}
                style={{ display: 'none' }}
              />
            </label>
          </div>

          {/* Budget (only for shopping) & Deadline */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: formData.request_type === 'shopping' ? '1fr 1fr' : '1fr',
            gap: 'var(--space-m)',
            marginBottom: 'var(--space-l)'
          }}>
            {formData.request_type === 'shopping' && (
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 'var(--space-s)',
                  color: 'var(--mid-gray)'
                }}>
                  Бюджет
                </label>
                <input
                  type="text"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  placeholder="Например: до 30 000 ₽"
                  style={{
                    width: '100%',
                    padding: 'var(--space-m)',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    border: '1px solid var(--soft-gray)'
                  }}
                />
              </div>
            )}
            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 'var(--space-s)',
                color: 'var(--mid-gray)'
              }}>
                Дедлайн
              </label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                style={{
                  width: '100%',
                  padding: 'var(--space-m)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  border: '1px solid var(--soft-gray)'
                }}
              />
            </div>
          </div>

          {error && (
            <p style={{
              color: '#ef4444',
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              marginBottom: 'var(--space-m)'
            }}>
              {error}
            </p>
          )}

          <div style={{ display: 'flex', gap: 'var(--space-m)' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '14px',
                backgroundColor: 'transparent',
                border: '1px solid var(--soft-gray)',
                fontFamily: 'var(--font-body)',
                cursor: 'pointer'
              }}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving || !formData.description.trim()}
              style={{
                flex: 1,
                padding: '14px',
                backgroundColor: 'var(--black)',
                color: 'var(--white)',
                border: 'none',
                fontFamily: 'var(--font-body)',
                cursor: saving ? 'wait' : 'pointer',
                opacity: saving || !formData.description.trim() ? 0.5 : 1
              }}
            >
              {saving ? 'Создание...' : 'Создать запрос'}
            </button>
          </div>
        </form>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

export default CreateRequestModal;
